import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle2,
  History,
  RefreshCw,
  Search,
  TriangleAlert,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BookingsTabs } from '@/features/manage-bookings/bookings-tabs';
import { BOOKING_STATUS } from '@/features/manage-bookings/types';
import { formatCurrency, formatDateTime, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CompDialog } from './comp-dialog';
import { ReconcileDialog } from './reconcile-dialog';
import {
  useAttachPayment,
  useCancelUnpaid,
  usePaymentReview,
  useRejectPayment,
  useSweep,
} from './payment-review.api';
import type { PaymentReviewItem, PayPalCandidate } from './types';

/**
 * Payment review — the standing list of bookings that should have been paid for
 * and have no payment recorded.
 *
 * Every row ends in a decision:
 *   a payment fits          → attach it, or rule it out
 *   nothing fits (any more) → confirm it was a comp, or cancel the booking
 *
 * The list splits on that distinction rather than filtering, because the two
 * halves take different actions and reading one does not help with the other.
 */

function StatusBadge({ status }: { status: number | null }) {
  const { t } = useTranslation();

  if (status === BOOKING_STATUS.confirmed) {
    return <Badge variant="success">{t('paymentReview.confirmed')}</Badge>;
  }
  if (status === BOOKING_STATUS.pending) {
    return <Badge variant="warning">{t('paymentReview.pending')}</Badge>;
  }
  return <Badge variant="destructive">{t('paymentReview.cancelled')}</Badge>;
}

/** One figure from the header summary. */
function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warning';
}) {
  return (
    <div className="bg-card rounded-xl border p-3">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p
        className={`text-xl font-semibold tabular-nums ${tone === 'warning' ? 'text-warning' : ''}`}
      >
        {value}
      </p>
    </div>
  );
}

/** The booking's own facts, shared by both halves of the list. */
function BookingFacts({ item }: { item: PaymentReviewItem }) {
  const { t } = useTranslation();

  return (
    <div className="min-w-0 space-y-1">
      <p className="truncate font-medium">{item.userName}</p>
      <p className="text-muted-foreground truncate text-xs">{item.userEmail}</p>
      <p className="text-muted-foreground text-xs">
        {item.eventName} · {formatDateTime(item.timeslotStartTime)}
      </p>
      <p className="text-muted-foreground text-xs">
        {t('paymentReview.bookedAt')}: {formatDateTime(item.bookedAt)}
      </p>
    </div>
  );
}

export function PaymentReviewPage() {
  const { t } = useTranslation();

  const review = usePaymentReview();
  const sweep = useSweep();
  const attach = useAttachPayment();
  const reject = useRejectPayment();
  const cancelUnpaid = useCancelUnpaid();

  const [comping, setComping] = useState<PaymentReviewItem | null>(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const data = review.data;
  const items = data?.items ?? [];
  const withCandidates = items.filter((i) => (i.candidates ?? []).length > 0);
  const withoutCandidates = items.filter(
    (i) => (i.candidates ?? []).length === 0
  );

  const failed = () => toast.error(t('paymentReview.actionFailed'));
  const busy = attach.isPending || reject.isPending || cancelUnpaid.isPending;
  // The queue on screen being re-read, as opposed to a first load.
  const refreshing = review.isFetching && !review.isPending;

  function runSweep() {
    sweep.mutate(undefined, {
      onSuccess: (result) =>
        toast.success(
          t('paymentReview.sweepFinished', {
            searched: formatNumber(result?.candidateBookings ?? 0),
            found: formatNumber((result?.matches ?? []).length),
          })
        ),
      onError: failed,
    });
  }

  /** Attach or rule out one suggested payment. */
  function CandidateRow({
    item,
    candidate,
  }: {
    item: PaymentReviewItem;
    candidate: PayPalCandidate;
  }) {
    return (
      <div className="bg-muted/50 space-y-2 rounded-lg p-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            <p className="font-mono text-xs break-all">
              {candidate.transactionId}
            </p>
            {candidate.invoiceId &&
              candidate.invoiceId !== candidate.transactionId && (
                <p className="text-muted-foreground font-mono text-xs break-all">
                  {candidate.invoiceId}
                </p>
              )}
            <p className="text-xs break-all">{candidate.payerEmail}</p>
            <p className="text-muted-foreground text-xs">
              {formatDateTime(candidate.transactionDate)}
            </p>
          </div>
          <p className="shrink-0 text-sm font-semibold tabular-nums">
            {formatCurrency(candidate.amount)}
          </p>
        </div>

        {/* Attaching a contested payment takes it away from the other booking
            it fits, so say so before the tap rather than after. */}
        {candidate.alsoFitsOtherBookings && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="warning">
                <TriangleAlert className="size-3" />
                {t('paymentReview.alsoFitsOtherBookings')}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t('paymentReview.alsoFitsOtherBookingsHelp')}
            </TooltipContent>
          </Tooltip>
        )}

        <div className="flex flex-wrap gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" disabled={busy}>
                {t('paymentReview.attachPayment')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('paymentReview.attachPayment')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('paymentReview.attachPaymentPrompt', {
                    amount: formatCurrency(candidate.amount),
                    name: item.userName ?? '',
                  })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    attach.mutate(
                      {
                        bookingId: item.bookingId,
                        transactionId: candidate.transactionId,
                      },
                      {
                        onSuccess: () =>
                          toast.success(t('paymentReview.paymentAttached')),
                        onError: failed,
                      }
                    )
                  }
                >
                  {t('common.yes')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={busy}>
                {t('paymentReview.notThisOne')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('paymentReview.notThisOne')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('paymentReview.rejectPaymentPrompt')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    reject.mutate(
                      {
                        bookingId: item.bookingId,
                        transactionId: candidate.transactionId,
                      },
                      {
                        onSuccess: () =>
                          toast.success(t('paymentReview.paymentRuledOut')),
                        onError: failed,
                      }
                    )
                  }
                >
                  {t('common.yes')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('paymentReview.title')}
        description={t('paymentReview.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReconcileOpen(true)}
            >
              <History />
              <span className="hidden sm:inline">
                {t('paymentReview.findOldPayments')}
              </span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={runSweep}
              disabled={sweep.isPending}
            >
              <Search />
              <span className="hidden sm:inline">
                {t('paymentReview.searchPaypalNow')}
              </span>
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('paymentReview.refresh')}
              onClick={() => void review.refetch()}
              disabled={review.isFetching}
            >
              <RefreshCw className={review.isFetching ? 'animate-spin' : ''} />
            </Button>
          </>
        }
      />

      <BookingsTabs />

      <p className="text-muted-foreground text-sm">
        {data?.lastSearchedAt
          ? t('paymentReview.lastSearchedAt', {
              when: formatDateTime(data.lastSearchedAt),
            })
          : t('paymentReview.neverSearched')}
      </p>

      {review.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('paymentReview.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void review.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {review.isPending && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-[68px] rounded-xl" />
            ))}
          </div>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-32 rounded-xl" />
          ))}
        </div>
      )}

      {/* Everything the review answers, refreshed as one: the reload button
          and every attach, reject or cancel re-read it. */}
      {data && (
        <div className="relative">
          <BusyOverlay show={refreshing} />
          <div
            aria-busy={refreshing}
            className={cn(
              'space-y-6',
              refreshing && 'opacity-60 transition-opacity'
            )}
          >
            {data && (
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <Stat
                  label={t('paymentReview.openReviews')}
                  value={formatNumber(data.open)}
                />
                <Stat
                  label={t('paymentReview.found')}
                  value={formatNumber(data.withCandidates)}
                />
                <Stat
                  label={t('paymentReview.noMatch')}
                  value={formatNumber(data.withoutCandidates)}
                />
                <Stat
                  label={t('paymentReview.amountAtStake')}
                  value={formatCurrency(data.amountAtStake)}
                  tone="warning"
                />
              </div>
            )}

            {data && items.length === 0 && (
              <div className="border-success/30 bg-success/10 flex items-start gap-2 rounded-xl border p-4 text-sm">
                <CheckCircle2 className="text-success mt-0.5 size-4 shrink-0" />
                <p>{t('paymentReview.allClear')}</p>
              </div>
            )}

            {/* ------------------------------------------ a payment probably fits */}
            {withCandidates.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {t('paymentReview.found')} ({formatNumber(withCandidates.length)})
                </h3>
                <div className="border-warning/30 bg-warning/10 flex items-start gap-2 rounded-xl border p-3 text-sm">
                  <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
                  <p>{t('paymentReview.foundHelp')}</p>
                </div>

                {/* The suggested payments sit inline rather than behind a disclosure:
                    they are the point of the row, so hiding them would ask someone to
                    tap through just to learn whether there is anything to decide. */}
                <div className="space-y-3">
                  {withCandidates.map((item) => (
                    <div
                      key={item.reviewId}
                      className="bg-card space-y-3 rounded-xl border p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <BookingFacts item={item} />
                        <p className="shrink-0 font-semibold tabular-nums">
                          {formatCurrency(item.price)}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={item.bookingStatus} />
                        {/* Not the booking's status — this is about the suggested
                            payment, a different question entirely. */}
                        {(item.candidates ?? []).length > 1 ? (
                          <Badge variant="warning">
                            <TriangleAlert className="size-3" />
                            {t('paymentReview.needsAChoice')}
                          </Badge>
                        ) : (
                          <Badge>{t('paymentReview.readyToAttach')}</Badge>
                        )}
                      </div>

                      {item.notes && (
                        <p className="text-muted-foreground text-xs">{item.notes}</p>
                      )}
                      {(item.rejectedTransactionIds ?? []).length > 0 && (
                        <p className="text-muted-foreground text-xs">
                          {t('paymentReview.ruledOutCount', {
                            count: item.rejectedTransactionIds!.length,
                          })}
                        </p>
                      )}

                      <div className="space-y-2 border-t pt-3">
                        {(item.candidates ?? []).map((candidate) => (
                          <CandidateRow
                            key={candidate.transactionId}
                            item={item}
                            candidate={candidate}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* -------------------------------------------------- nothing fits it */}
            {withoutCandidates.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {t('paymentReview.noMatch')} (
                  {formatNumber(withoutCandidates.length)})
                </h3>
                <div className="bg-muted flex items-start gap-2 rounded-xl border p-3 text-sm">
                  <AlertCircle className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                  <p>{t('paymentReview.noMatchHelp')}</p>
                </div>

                <div className="space-y-2">
                  {withoutCandidates.map((item) => (
                    <div
                      key={item.reviewId}
                      className="bg-card space-y-3 rounded-xl border p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <BookingFacts item={item} />
                        <p className="shrink-0 font-semibold tabular-nums">
                          {formatCurrency(item.price)}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={item.bookingStatus} />
                        {/* An open payment request means the customer may simply not
                            have paid yet; without one, nobody ever asked them to. */}
                        {item.hasUnpaidPurchase ? (
                          <Badge variant="warning">
                            {t('paymentReview.awaitingPayment')}
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            {t('paymentReview.needsReview')}
                          </Badge>
                        )}
                        <span className="text-muted-foreground text-xs">
                          {t('paymentReview.searchedTimes', {
                            count: item.searchCount ?? 0,
                          })}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => setComping(item)}
                        >
                          {t('paymentReview.confirmComped')}
                        </Button>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive"
                              disabled={busy}
                            >
                              {t('paymentReview.cancelBooking')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t('paymentReview.cancelBooking')}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('paymentReview.cancelBookingPrompt', {
                                  name: item.userName ?? '',
                                })}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {t('common.cancel')}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() =>
                                  cancelUnpaid.mutate(item.bookingId, {
                                    onSuccess: () =>
                                      toast.success(
                                        t('paymentReview.bookingCancelled')
                                      ),
                                    onError: failed,
                                  })
                                }
                              >
                                {t('common.yes')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      <CompDialog
        item={comping}
        open={comping !== null}
        onOpenChange={(next) => {
          if (!next) setComping(null);
        }}
      />

      <ReconcileDialog open={reconcileOpen} onOpenChange={setReconcileOpen} />
    </div>
  );
}
